import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import { PostCardSkeleton } from '@/components/ui/skeleton';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { PostCard } from '@/components/PostCard';

interface Post {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail: string;
  category: string[];
  date: string;
}

const POSTS_PER_PAGE = 6;

const Posts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCategory, setCurrentCategory] = useState(searchParams.get('category') || 'All');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['All']);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [allPosts, currentCategory, searchTerm]);

  useEffect(() => {
    const urlCategory = searchParams.get('category');
    const urlSearch = searchParams.get('search');
    
    if (urlCategory && urlCategory !== currentCategory) {
      setCurrentCategory(urlCategory);
    }
    
    if (urlSearch !== null && urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
    }
  }, [searchParams]);

  const fetchPosts = async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedPosts = data?.map(post => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        description: post.description,
        thumbnail: post.thumbnail,
        category: post.category,
        date: post.created_at,
      })) || [];

      setAllPosts(transformedPosts);
      
      // Extract unique categories
      const uniqueCategories = new Set<string>(['All']);
      transformedPosts.forEach((post: Post) => {
        post.category.forEach(cat => uniqueCategories.add(cat));
      });
      setCategories(Array.from(uniqueCategories));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = allPosts.filter(post => {
      const matchesCategory = currentCategory === 'All' || post.category.includes(currentCategory);
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setFilteredPosts(filtered);
  };

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    setCurrentPage(1);
    
    if (category === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "InvizHer Blog",
    "description": "Programming tutorials, coding projects, and development resources",
    "url": "https://invizher.com/posts",
    "publisher": {
      "@type": "Organization",
      "name": "InvizHer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://invizher.com/favicon.ico"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Posts | InvizHer - Programming Tutorials & Development Resources</title>
        <meta name="description" content="Explore comprehensive programming tutorials, coding guides, web development articles, and tech resources. Learn JavaScript, Python, React, and more." />
        <meta name="keywords" content="programming tutorials, coding guides, web development, JavaScript tutorials, Python guides, React tutorials, tech blog" />
        <link rel="canonical" href="https://invizher.com/posts" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://invizher.com/posts" />
        <meta property="og:title" content="Programming Tutorials & Articles | InvizHer" />
        <meta property="og:description" content="Explore comprehensive programming tutorials, coding guides, and web development articles." />
        <meta property="og:site_name" content="InvizHer" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Programming Tutorials | InvizHer" />
        <meta name="twitter:description" content="Explore comprehensive programming tutorials and coding guides." />
        
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
      </Helmet>

      <div>
        {/* Dynamic Background */}
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 left-0 w-screen h-screen opacity-10">
            <div className="absolute top-0 left-1/4 w-90 h-90 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl"></div>
            <div className="absolute top-1/3 right-1/4 w-90 h-90 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl"></div>
            <div className="absolute bottom-1/4 left-1/3 w-90 h-90 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl"></div>
          </div>
        </div>

        <Header />

        <main className="pt-28" role="main">
          <div className="container mx-auto px-4 mb-14 max-w-7xl">
            {/* Page Header */}
            <div className="container mx-auto px-4">
              <div className="text-center py-8">
                <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Discover Amazing Content
                </h1>
                <p className="text-gray-600">Explore our collection of thoughtfully curated articles</p>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="mx-auto max-w-4xl mb-8">
              <div className="rounded-2xl p-4 md:p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Search Bar */}
                  <div className="relative flex-1" role="search">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="search-input w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                      placeholder="Search posts..."
                    />
                    {searchTerm && (
                      <button 
                        onClick={() => setSearchTerm('')}
                        className="search-clear absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <i className="fas fa-search"></i>
                    </div>
                  </div>
                </div>
                
                {/* Categories */}
                <div className="mt-4 categories-container">
                  <div className="flex gap-2 min-w-max px-1 py-2">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => handleCategoryChange(category)}
                        className={`category-pill px-4 py-2 rounded-full glass hover:border-blue-500 transition-all flex items-center ${
                          currentCategory === category ? 'active selected' : ''
                        }`}
                      >
                        <span>{category}</span>
                        {currentCategory === category && category !== 'All' && (
                          <span 
                            className="deselect-btn ml-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryChange('All');
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Search Results Message */}
            {(searchTerm || currentCategory !== 'All') && (
              <div className="search-results-message glass rounded-xl mx-2 show">
                <div className="container max-w-4xl mx-auto px-6 py-3">
                  <div className="flex items-center justify-between">
                    <p className="text-gray-700">
                      {searchTerm && <>Results for "<span className="search-query">{searchTerm}</span>"</>}
                    </p>
                    <span className="text-sm text-gray-500">
                      {filteredPosts.length} results found{currentCategory !== 'All' && ` in ${currentCategory}`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 max-w-6xl mx-auto px-2">
              {loading ? (
                // Skeleton loaders
                Array(POSTS_PER_PAGE).fill(0).map((_, i) => (
                  <PostCardSkeleton key={i} />
                ))
              ) : paginatedPosts.length > 0 ? (
                paginatedPosts.map(post => <PostCard key={post.id} post={{ ...post, category: post.category }} />)
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-gray-500">
                    <i className="fas fa-search text-4xl mb-4"></i>
                    <p className="text-xl">No posts found matching your criteria</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <div className="flex gap-2">
                  <button
                    className={`px-4 py-2 rounded-lg glass ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500'}`}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <button
                          key={page}
                          className={`px-4 py-2 rounded-lg border ${page === currentPage ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : 'border-gray-200 hover:border-blue-500'}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-4 py-2">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    className={`px-4 py-2 rounded-lg glass ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-500'}`}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Posts;
